import { CompositionDto } from "@repo/api/editor";
import { AnimatedImageDto } from "@repo/api/animated-image";
import {
  ElementDto,
  ElementImageTimingDto,
  ElementTimingDto,
  SceneTypeDto,
} from "@repo/api/overlays";
import {
  getAnimatedImageConfig,
  getElementsByAspectRatio,
} from "@repo/api/utils/composition";
import { ImageTransitionBuilders } from "../components/ImageTransitionBuilder/ImageTransitionBuilder.ts";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";

export type CompositionForTimings = Pick<
  CompositionDto,
  | "fps"
  | "aspectRatio"
  | "mainSceneTimeline"
  | "startScene"
  | "endScene"
  | "elements"
>;

export type AnimatedImageForTimings = Pick<AnimatedImageDto, "durationMs">;

export const msToFrames = (ms: number, fps: number) => {
  if (fps === 0) {
    throw new Error("FPS cannot be 0");
  }

  if (ms <= 0) {
    return 0;
  }

  return Math.max(0, Math.floor((ms / 1000) * fps));
};

export const framesToMs = (frames: number, fps: number) => {
  if (fps === 0) {
    throw new Error("FPS cannot be 0");
  }

  if (frames <= 0) {
    return 0;
  }

  return Math.max(0, Math.floor((frames * 1000) / fps));
};

export const DURATION_IMAGE_DEFAULT = 2500;

export const calculateDurationInFrames = (
  composition: CompositionForTimings,
  animatedImages: Record<string, AnimatedImageForTimings>,
) => {
  const compositionTimings = getCompositionTimings(composition, animatedImages);
  return compositionTimings.durationInFrames;
};

export const getCompositionDurationMs = (
  composition: CompositionForTimings,
  animatedImages: Record<string, AnimatedImageForTimings>,
) => {
  try {
    const { durationInFrames, fps } = getCompositionTimings(
      composition,
      animatedImages,
    );
    return fps > 0 ? (durationInFrames / fps) * 1000 : 0;
  } catch {
    return 0;
  }
};

export interface WordWithStartTime {
  word: string;
  startTime: number;
  startIndex: number;
}

export const getWordStartTimes = (
  text: string,
  characterStartTimes: number[],
) => {
  const words: WordWithStartTime[] = [];

  let word = "";
  let startTime = 0;
  let startIndex = 0;

  for (let i = 0; i < text.length; i++) {
    const char = text[i]!;
    const isWhiteSpace = /\s/.test(char);

    if (isWhiteSpace) {
      if (word.length > 0) {
        words.push({
          word,
          startTime,
          startIndex,
        });

        word = "";
        startTime = 0;
      }

      continue;
    }

    if (word === "") {
      word = char;
      startTime = characterStartTimes[i]!;
      startIndex = i;
    } else {
      word += char;
    }
  }

  if (word !== "") {
    words.push({
      word,
      startTime,
      startIndex,
    });
  }

  return words;
};

/**
 * Audio tags (e.g. [whispers], [pause]) are instructions inside brackets, not caption text. They're replaced
 * with spaces so the text stays aligned with characterStartTimes.
 */
export function removeAudioTags(text: string) {
  return text.replace(/\[[^[\]]*\]/g, (tag) => " ".repeat(tag.length));
}

export type CompositionImageTiming = {
  timelineItemExternalId: string;
  animatedImageExternalId: string | undefined;
  entryTransitionFrames: number;
  exitTransitionFrames: number;
  globalStartAtFrame: number;
  globalEndAtFrame: number;
  durationInFrames: number;
  reverse: boolean;
};

type ImageTimingParams = {
  timelineItem: CreativeProjectTimelineItem;
  nextTimelineItem: CreativeProjectTimelineItem | null;
  animatedImage: AnimatedImageForTimings | undefined;
  globalCurrentFrame: number;
  fps: number;
  index: number;
};

function getCompositionImageTiming({
  timelineItem,
  nextTimelineItem,
  animatedImage,
  globalCurrentFrame,
  fps,
  index,
}: ImageTimingParams): CompositionImageTiming {
  let durationInFrames: number;
  let reverse: boolean;

  if (!timelineItem.animatedImageExternalId) {
    durationInFrames = msToFrames(DURATION_IMAGE_DEFAULT, fps);
    reverse = false;
  } else {
    const config = getAnimatedImageConfig(timelineItem);

    durationInFrames = msToFrames(
      config.duration
        ? Math.min(
            config.duration * 1000,
            getMaxDurationMs(animatedImage!, config),
          )
        : DURATION_IMAGE_DEFAULT,
      fps,
    );

    reverse = config.reverse;
  }

  let entryTransitionFrames = 0;

  if (index > 0 && timelineItem.entryTransition) {
    const imageTransitionBuilder = ImageTransitionBuilders.get(
      timelineItem.entryTransition.kind,
    );
    entryTransitionFrames =
      imageTransitionBuilder?.transition?.getDurationInFrames({
        imageTransition: timelineItem.entryTransition,
        fps,
      }) ?? 0;
  }

  let exitTransitionFrames = 0;

  if (nextTimelineItem?.entryTransition) {
    const imageTransitionBuilder = ImageTransitionBuilders.get(
      nextTimelineItem.entryTransition.kind,
    );
    exitTransitionFrames =
      imageTransitionBuilder?.transition?.getDurationInFrames({
        imageTransition: nextTimelineItem.entryTransition,
        fps,
      }) ?? 0;
  }

  const globalStartAtFrame = Math.max(
    0,
    globalCurrentFrame - entryTransitionFrames,
  );

  const safeDurationInFrames = Math.max(
    durationInFrames,
    entryTransitionFrames + exitTransitionFrames + 1,
  );

  const globalEndAtFrame = globalStartAtFrame + safeDurationInFrames - 1;

  return {
    timelineItemExternalId: timelineItem.externalId,
    animatedImageExternalId: timelineItem.animatedImageExternalId,
    entryTransitionFrames,
    exitTransitionFrames,
    globalStartAtFrame,
    globalEndAtFrame,
    durationInFrames: safeDurationInFrames,
    reverse,
  };
}

export function getMaxDurationMs(
  animatedImage: AnimatedImageForTimings,
  config: CreativeProjectTimelineItem["animatedImageConfigs"][string],
) {
  if (animatedImage.durationMs === 0) {
    return DURATION_IMAGE_DEFAULT;
  }

  return animatedImage.durationMs / (config.playbackSpeed ?? 1);
}

type ElementTimingParams = {
  element: ElementDto;
  compositionImageTimings: CompositionImageTiming[];
  globalStartAtFrame: number;
  globalEndAtFrame: number;
  fps: number;
};

export type ElementTiming = {
  globalStartAtFrame: number;
  globalEndAtFrame: number;
  sceneStartAtFrame: number;
  sceneEndAtFrame: number;
  durationInFrames: number;
  entryTransitionFrames: number;
  exitTransitionFrames: number;
};

function getElementTiming({
  element,
  compositionImageTimings,
  globalStartAtFrame,
  globalEndAtFrame,
  fps,
}: ElementTimingParams): ElementTiming | null {
  const enterAtTiming = element.enterAt.timing;
  const exitAtTiming = element.exitAt.timing;

  let startFrame: number | null;
  let endFrame: number | null;

  if (enterAtTiming) {
    startFrame =
      parseElementTimingFrame({
        elementTiming: enterAtTiming,
        compositionImageTimings,
        globalStartAtFrame,
        fps,
      }) ?? globalStartAtFrame;
  } else {
    startFrame = globalStartAtFrame;
  }

  if (exitAtTiming) {
    endFrame =
      parseElementTimingFrame({
        elementTiming: exitAtTiming,
        compositionImageTimings,
        globalStartAtFrame,
        fps,
      }) ?? globalEndAtFrame;
  } else {
    endFrame = globalEndAtFrame;
  }

  if (startFrame > globalEndAtFrame) {
    return null;
  }

  if (startFrame < globalStartAtFrame) {
    startFrame = globalStartAtFrame;
  }

  if (endFrame < startFrame || endFrame < globalStartAtFrame) {
    return null;
  }

  if (endFrame > globalEndAtFrame) {
    endFrame = globalEndAtFrame;
  }

  const entryTransitionFrames = msToFrames(
    (element.enterAt.animation?.duration ?? 0) * 1000,
    fps,
  );

  const exitTransitionFrames = msToFrames(
    (element.exitAt.animation?.duration ?? 0) * 1000,
    fps,
  );

  const durationInFrames = endFrame - startFrame + 1;

  return {
    globalStartAtFrame: startFrame,
    globalEndAtFrame: endFrame,
    sceneStartAtFrame: startFrame - globalStartAtFrame,
    sceneEndAtFrame: endFrame - globalStartAtFrame,
    durationInFrames,
    entryTransitionFrames,
    exitTransitionFrames,
  };
}

type ParseElementTimingParams = {
  elementTiming: ElementTimingDto;
  compositionImageTimings: CompositionImageTiming[];
  globalStartAtFrame: number;
  fps: number;
};

function parseElementTimingFrame({
  elementTiming,
  compositionImageTimings,
  globalStartAtFrame,
  fps,
}: ParseElementTimingParams) {
  if (elementTiming.type === "SECOND_TIMING") {
    return globalStartAtFrame + msToFrames(elementTiming.seconds * 1000, fps);
  }

  const imageTiming = findImageTiming(compositionImageTimings, elementTiming);

  if (!imageTiming) {
    return null;
  }

  const offsetFrames = msToFrames(elementTiming.seconds * 1000, fps);

  return imageTiming.globalStartAtFrame + offsetFrames;
}

export function findImageTiming(
  compositionImageTimings: CompositionImageTiming[],
  elementTiming: ElementImageTimingDto,
) {
  const numImages = compositionImageTimings.length;

  const index =
    elementTiming.countFrom === "FIRST"
      ? elementTiming.imageIndex
      : numImages - elementTiming.imageIndex - 1;

  if (index < 0 || index >= numImages) {
    return null;
  }

  return compositionImageTimings[index];
}

export type StartSceneTiming = {
  globalStartAtFrame: number;
  globalEndAtFrame: number;
  durationInFrames: number;
  exitTransitionFrames: number;
  elementTimings: Record<string, ElementTiming | null>;
};
export type MainSceneTiming = {
  globalStartAtFrame: number;
  globalEndAtFrame: number;
  durationInFrames: number;
  entryTransitionFrames: number;
  exitTransitionFrames: number;
  compositionImageTimings: CompositionImageTiming[];
  elementTimings: Record<string, ElementTiming | null>;
};
export type EndSceneTiming = {
  globalStartAtFrame: number;
  globalEndAtFrame: number;
  durationInFrames: number;
  entryTransitionFrames: number;
  elementTimings: Record<string, ElementTiming | null>;
};

export type CompositionTimings = {
  fps: number;
  startScene: StartSceneTiming | null;
  mainScene: MainSceneTiming;
  endScene: EndSceneTiming | null;
  durationInFrames: number;
};

export function getVoiceOverStartFrame(
  timings: CompositionTimings,
  compositionVoiceOver: CompositionDto["voiceOver"],
  fps: number,
) {
  const { globalStartAtFrame, entryTransitionFrames } = timings.mainScene;

  return (
    globalStartAtFrame +
    entryTransitionFrames +
    (entryTransitionFrames === 0 ? msToFrames(500, fps) : 0) +
    msToFrames(compositionVoiceOver?.startSilenceMs ?? 0, fps)
  );
}

export function getCompositionTimings(
  composition: CompositionForTimings,
  animatedImages: Record<string, AnimatedImageForTimings>,
): CompositionTimings {
  const startSceneTimings = getStartSceneTimings(composition);

  const mainSceneTimings = getMainSceneTimings(
    composition,
    animatedImages,
    startSceneTimings,
  );

  const endSceneTimings = getEndSceneTimings(
    composition,
    mainSceneTimings,
    startSceneTimings,
  );

  mainSceneTimings.entryTransitionFrames =
    startSceneTimings?.exitTransitionFrames ?? 0;
  mainSceneTimings.exitTransitionFrames =
    endSceneTimings?.entryTransitionFrames ?? 0;

  const startSceneFrames = startSceneTimings?.durationInFrames ?? 0;
  const mainSceneFrames =
    mainSceneTimings.durationInFrames - mainSceneTimings.entryTransitionFrames;
  const endSceneFrames =
    (endSceneTimings?.durationInFrames ?? 0) -
    (endSceneTimings?.entryTransitionFrames ?? 0);

  return {
    fps: composition.fps,
    startScene: startSceneTimings,
    mainScene: mainSceneTimings,
    endScene: endSceneTimings,
    durationInFrames: startSceneFrames + mainSceneFrames + endSceneFrames,
  };
}

function getStartSceneTimings(
  composition: CompositionForTimings,
): StartSceneTiming | null {
  const globalStartAtFrame = 0;
  const startScene = composition.startScene?.[composition.aspectRatio];

  if (!startScene) {
    return null;
  }

  const durationInFrames = msToFrames(
    startScene.duration.seconds * 1000,
    composition.fps,
  );

  const globalEndAtFrame = durationInFrames - 1;

  let exitTransitionFrames = 0;

  if (startScene.exitTransition) {
    exitTransitionFrames = msToFrames(
      startScene.exitTransition.duration * 1000,
      composition.fps,
    );
  }

  const elementTimings: Record<string, ElementTiming | null> = {};

  for (const element of startScene.elements) {
    elementTimings[element.externalId] = getElementTiming({
      element,
      compositionImageTimings: [],
      globalStartAtFrame,
      globalEndAtFrame,
      fps: composition.fps,
    });
  }

  return {
    globalStartAtFrame,
    globalEndAtFrame,
    durationInFrames,
    exitTransitionFrames,
    elementTimings,
  };
}

function getMainSceneTimings(
  composition: CompositionForTimings,
  animatedImages: Record<string, AnimatedImageForTimings>,
  startSceneTimings: StartSceneTiming | null,
): MainSceneTiming {
  let globalMainSceneStartAtFrame: number;

  if (startSceneTimings) {
    globalMainSceneStartAtFrame =
      startSceneTimings.globalEndAtFrame -
      startSceneTimings.exitTransitionFrames +
      1;
  } else {
    globalMainSceneStartAtFrame = 0;
  }

  let globalCurrentFrame = globalMainSceneStartAtFrame;

  const compositionImageTimings = composition.mainSceneTimeline.map(
    (item, index) => {
      const nextItem =
        composition.mainSceneTimeline.length > index + 1
          ? composition.mainSceneTimeline[index + 1]!
          : null;

      const animatedImage = item.animatedImageExternalId
        ? animatedImages[item.animatedImageExternalId]
        : undefined;

      const timing = getCompositionImageTiming({
        timelineItem: item,
        nextTimelineItem: nextItem,
        animatedImage: animatedImage,
        globalCurrentFrame,
        fps: composition.fps,
        index,
      });

      globalCurrentFrame = timing.globalEndAtFrame + 1;

      return timing;
    },
  );

  const globalMainSceneEndAtFrame = globalCurrentFrame - 1;

  const elementTimings: Record<string, ElementTiming | null> = {};

  const elements = getElementsByAspectRatio(composition, "mainScene");

  for (const element of elements) {
    elementTimings[element.externalId] = getElementTiming({
      element,
      compositionImageTimings,
      globalStartAtFrame: globalMainSceneStartAtFrame,
      globalEndAtFrame: globalMainSceneEndAtFrame,
      fps: composition.fps,
    });
  }

  return {
    globalStartAtFrame: globalMainSceneStartAtFrame,
    globalEndAtFrame: globalMainSceneEndAtFrame,
    durationInFrames:
      globalMainSceneEndAtFrame - globalMainSceneStartAtFrame + 1,
    entryTransitionFrames: 0,
    exitTransitionFrames: 0,
    compositionImageTimings,
    elementTimings,
  };
}

function getEndSceneTimings(
  composition: CompositionForTimings,
  mainSceneTimings: MainSceneTiming,
  startSceneTimings: StartSceneTiming | null,
): EndSceneTiming | null {
  const endScene = composition.endScene?.[composition.aspectRatio];

  if (!endScene) {
    return null;
  }

  const durationInFrames = msToFrames(
    endScene.duration.seconds * 1000,
    composition.fps,
  );

  let entryTransitionFrames = 0;

  if (endScene.entryTransition) {
    const maxEntryTransitionFrames = Math.max(
      0,
      mainSceneTimings.durationInFrames -
        (startSceneTimings?.exitTransitionFrames ?? 0) -
        1,
    );

    entryTransitionFrames = Math.min(
      msToFrames(endScene.entryTransition.duration * 1000, composition.fps),
      maxEntryTransitionFrames,
    );
  }

  const globalStartAtFrame =
    mainSceneTimings.globalEndAtFrame - entryTransitionFrames + 1;

  const globalEndAtFrame = globalStartAtFrame + durationInFrames - 1;

  const elementTimings: Record<string, ElementTiming | null> = {};

  for (const element of endScene.elements) {
    elementTimings[element.externalId] = getElementTiming({
      element,
      compositionImageTimings: [],
      globalStartAtFrame,
      globalEndAtFrame,
      fps: composition.fps,
    });
  }

  return {
    globalStartAtFrame,
    globalEndAtFrame,
    durationInFrames,
    entryTransitionFrames,
    elementTimings,
  };
}

export function findElementTiming(
  compositionTimings: CompositionTimings,
  elementExternalId: string,
  sceneType: SceneTypeDto,
) {
  if (sceneType === "mainScene") {
    return compositionTimings.mainScene.elementTimings[elementExternalId];
  }

  if (sceneType === "startScene") {
    return (
      compositionTimings.startScene?.elementTimings[elementExternalId] ?? null
    );
  }

  return compositionTimings.endScene?.elementTimings[elementExternalId] ?? null;
}
